import { MenuItem, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { foyerApi } from "../../http/foyer";

/**
 * Which of the company's feeds an upload is filed under (Sigge, 2026-10-19).
 *
 * A content owner is a company that may hold several feeds - Tigrar is four
 * labels - and each title is reported under exactly one. With a single feed
 * there is nothing to ask and this renders nothing (the server files it under
 * that one); with several, the choice is required, because the server refuses
 * to guess (`feed_required`).
 *
 * `onFeeds` reports how many there are, so the caller can hold its submit
 * until a choice is made.
 */
export function useOwnerFeeds(enabled: boolean) {
  const [feeds, setFeeds] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    foyerApi.listOwnFeeds().then(
      (res) => live && setFeeds(res.feeds),
      () => live && setFeeds([])
    );
    return () => {
      live = false;
    };
  }, [enabled]);
  return feeds;
}

export default function OwnerFeedSelect({
  feeds,
  value,
  onChange,
  disabled,
}: {
  feeds: Array<{ id: string; name: string }>;
  value: string;
  onChange: (feedId: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("supplier");
  if (feeds.length <= 1) return null;
  return (
    <TextField
      select
      size="small"
      label={t("ownerFeedSelect.label")}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      disabled={disabled}
      error={value === ""}
      helperText={t("ownerFeedSelect.help")}
      inputProps={{ "data-testid": "owner-feed-select" }}
      sx={{ minWidth: 220 }}
    >
      {feeds.map((f) => (
        <MenuItem key={f.id} value={f.id}>
          {f.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** True when the upload may go: one feed (or none - the server says why), or a choice made. */
export function feedChoiceReady(
  feeds: Array<{ id: string; name: string }>,
  value: string
): boolean {
  return feeds.length <= 1 || value !== "";
}
